import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchBar } from "@/components/search-bar";
import { DataTable } from "@/components/data-table";
import { getSourcesColumns } from "@/components/configuration/sources-columns";
import {
  SourceDialog,
  type SourceFormData,
} from "@/components/configuration/source-dialog";
import { SourceDeleteDialog } from "@/components/configuration/source-delete-dialog";
import { useSources } from "@/hooks/use-sources";
import { useCreateSource } from "@/hooks/use-create-source";
import { useUpdateSource } from "@/hooks/use-update-source";
import { useDeleteSource } from "@/hooks/use-delete-source";
import { useAdminAccess } from "@/hooks/use-admin-access";
import { usePathname, useRouter, useSearchParams } from "@/i18n/navigation";
import { getSourceDisplayName } from "@/lib/source-utils";
import client from "@/lib/api";
import type { Source } from "@/types";

export const Route = createFileRoute("/_dashboard/configuration/sources")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q.slice(0, 100) : undefined,
  }),
  component: SourcesPage,
});

function SourcesPage() {
  const t = useTranslations("SourcesPage");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawSearch = searchParams.toString();
  const search = (searchParams.get("q") ?? "").slice(0, 100);
  const {
    isAdmin,
    isLoading: isCheckingAccess,
    isError: accessError,
    refetch: refetchAccess,
  } = useAdminAccess();
  const {
    sources,
    isLoading,
    isFetching,
    isError: sourcesError,
    isTruncated,
    refetch,
  } = useSources({ enabled: isAdmin });
  const { createSource, isSubmitting: isCreating } = useCreateSource();
  const { updateSource, isSubmitting: isUpdating } = useUpdateSource();
  const { deleteSource, isDeleting } = useDeleteSource();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreatingEntity, setIsCreatingEntity] = useState(false);
  const [editing, setEditing] = useState<Source | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<Source | null>(null);

  const filteredSources = useMemo(() => {
    if (!search) return sources;
    const q = search.toLowerCase();
    return sources.filter(
      (s) =>
        getSourceDisplayName(s).toLowerCase().includes(q) ||
        s.location?.toLowerCase().includes(q)
    );
  }, [sources, search]);

  useEffect(() => {
    if (pathname !== "/configuration/sources") return;
    const expected = new URLSearchParams();
    if (search) expected.set("q", search);
    if (rawSearch !== expected.toString()) {
      router.replace(
        search
          ? `/configuration/sources?${expected.toString()}`
          : "/configuration/sources"
      );
    }
  }, [pathname, rawSearch, router, search]);

  function updateSearch(value: string) {
    const next = value.slice(0, 100);
    const params = new URLSearchParams();
    if (next) params.set("q", next);
    router.replace(
      next
        ? `/configuration/sources?${params.toString()}`
        : "/configuration/sources"
    );
  }

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  const openEdit = useCallback((source: Source) => {
    setEditing(source);
    setDialogOpen(true);
  }, []);

  const openDelete = useCallback((source: Source) => {
    setDeleting(source);
    setDeleteDialogOpen(true);
  }, []);

  async function handleSave(data: SourceFormData) {
    if (data.mode === "create") setIsCreatingEntity(true);
    try {
      if (data.mode === "edit") {
        if (!editing) return;

        await updateSource({
          id: editing.id,
          body: {
            location:
              data.location !== null
                ? { set: true, valid: true, value: data.location }
                : { set: true, valid: false },
            notes:
              data.notes !== null
                ? { set: true, valid: true, value: data.notes }
                : { set: true, valid: false },
          },
        });
        toast.success(t("success"));
        setDialogOpen(false);
        setEditing(null);
      } else {
        // The API links sources to canonical entities, so the entity must be
        // created first. These are separate backend transactions.
        let entityId: string | null = null;

        if (data.type === "person") {
          const { data: person, error } = await client.POST("/persons", {
            body: {
              first_name: data.first_name,
              last_name: data.last_name,
            },
          });
          if (error || !person) throw new Error("Failed to create person");
          entityId = person.id;
        } else {
          const { data: org, error } = await client.POST("/organizations", {
            body: {
              name: data.name,
              legal_name: data.legal_name,
            },
          });
          if (error || !org) throw new Error("Failed to create organization");
          entityId = org.id;
        }

        await createSource({
          type: data.type,
          ...(data.type === "person"
            ? { person_id: entityId }
            : { organization_id: entityId }),
          location: data.location,
          notes: data.notes,
        });
        toast.success(t("success"));
        setDialogOpen(false);
      }
    } catch {
      toast.error(t("error"));
    } finally {
      if (data.mode === "create") setIsCreatingEntity(false);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteSource(deleting.id);
      toast.success(t("deleteSuccess"));
      setDeleteDialogOpen(false);
      setDeleting(null);
    } catch {
      toast.error(t("deleteError"));
    }
  }

  const columns = useMemo(
    () => getSourcesColumns(t, openEdit, openDelete),
    [t, openEdit, openDelete]
  );

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="sr-only">{t("title")}</h1>

        {isCheckingAccess ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : accessError ? (
          <div
            role="alert"
            className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-md border p-6 text-center"
          >
            <p className="text-muted-foreground text-sm">
              {t("accessCheckError")}
            </p>
            <Button variant="outline" onClick={() => void refetchAccess()}>
              {t("retry")}
            </Button>
          </div>
        ) : !isAdmin ? (
          <div role="alert" className="rounded-md border p-4 text-sm">
            {t("adminOnly")}
          </div>
        ) : (
          <>
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <p className="text-muted-foreground text-sm">
                {t("description")}
              </p>
              <Button variant="outline" size="sm" onClick={openCreate}>
                <PlusIcon className="size-4" />
                {t("addSource")}
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <SearchBar
                value={search}
                onChange={updateSearch}
                placeholder={t("searchPlaceholder")}
              />
            </div>

            {isTruncated && (
              <div role="status" className="rounded-md border p-3 text-sm">
                {t("sourceLimitWarning")}
              </div>
            )}

            {sourcesError ? (
              <div
                role="alert"
                className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-md border p-6 text-center"
              >
                <p className="text-muted-foreground text-sm">
                  {t("loadError")}
                </p>
                <Button
                  variant="outline"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                >
                  {isFetching ? t("retrying") : t("retry")}
                </Button>
              </div>
            ) : isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            ) : filteredSources.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {search ? t("noSearchResults") : t("noSources")}
              </p>
            ) : (
              <DataTable
                columns={columns}
                data={filteredSources}
                onRowClick={(row) => router.push(`/cases?source=${row.id}`)}
              />
            )}

            <SourceDialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) setEditing(null);
              }}
              source={editing}
              onSave={handleSave}
              isSubmitting={isCreatingEntity || isCreating || isUpdating}
            />

            <SourceDeleteDialog
              open={deleteDialogOpen}
              onOpenChange={(open) => {
                setDeleteDialogOpen(open);
                if (!open) setDeleting(null);
              }}
              source={deleting}
              onConfirm={handleDelete}
              isDeleting={isDeleting}
            />
          </>
        )}
      </div>
    </>
  );
}
